import { useCallback, useMemo, useRef, useState } from 'react';
import { useCreateInternalStateBranch } from '../../state/InternalContext';
import { InternalState } from '../../state/state-models';
import { findFirstComponentOfType } from '../internal/TextExtractor';
import { ArrayProvider } from '../../state/ArrayContext';
import { SourceItem } from '../../shared/models/source-item.model';
import React from 'react';

interface ForEachProps {
  store: string,
  children: React.ReactNode,
}

/***
 * A component that iterates over an array of items and renders the content for each item.
 * The specification is used to load the array of items. It must be an ArraySource specification.
 */
export const ForEach = React.memo(function ForEach({ store, children }: ForEachProps) {
  const createInternalStateBranch = useCreateInternalStateBranch();

  // Get the of array from the of child
  const ofChild = useMemo(() => findFirstComponentOfType(children, "arg-of"), [children]);

  // Get the do child which contains the children to render for each item
  const doChild = useMemo(() => findFirstComponentOfType(children, "arg-do"), [children]);
  const doChildRef = useRef(doChild);
  doChildRef.current = doChild;

  const [array, setArray] = useState<Array<SourceItem>>([]);

  const areSourceItemValuesEqual = useCallback((left: SourceItem, right: SourceItem): boolean => {
    if (left.value instanceof Date && right.value instanceof Date) {
      return left.value.getTime() === right.value.getTime();
    }
    return left.value === right.value;
  }, []);

  const setStableArray = useCallback((nextArray: Array<SourceItem>) => {
    setArray((prevArray) => {
      if (prevArray.length !== nextArray.length) {
        return nextArray;
      }

      const hasDifferentValueOrOrder = prevArray.some((prevItem, index) => {
        return !areSourceItemValuesEqual(prevItem, nextArray[index]);
      });

      return hasDifferentValueOrOrder ? nextArray : prevArray;
    });
  }, [areSourceItemValuesEqual]);

  const internalBranchComponents = useMemo(() => {
    return array.map((item) => {
      const branchState = item.state as InternalState || {};
      if (store) {
        if (!branchState.storedItems) {
          branchState.storedItems = {};
        };
        branchState.storedItems[store] = { ...item, template: doChildRef.current };
      }
      return createInternalStateBranch(branchState);
    }) || [];
  }, [array, createInternalStateBranch, store]);

  const output = useMemo(() => {
    const child = doChild; // stable
    const content: React.ReactNode = child?.props.children;

    return array.map((item, index) => {
      const key = item.value.toString();
      const InternalBranchComponent = internalBranchComponents[index];

      return (
        <InternalBranchComponent key={key}>
          {content}
        </InternalBranchComponent>
      );
    });
  }, [array, internalBranchComponents, doChild]);

  return <>
    <ArrayProvider setArray={setStableArray}>
      {ofChild?.props.children}
    </ArrayProvider>
    {output}
  </>;
});